<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Exit Cases</h5>
        <p class="page-sub">All employee exit cases</p>
      </div>
      <div class="header-actions">
        <button class="btn-export-sm" @click="openExportModal('csv')" :disabled="exportLoading"><font-awesome-icon icon="file-csv" /> CSV</button>
        <button class="btn-export-sm btn-export-sm--pdf" @click="openExportModal('pdf')" :disabled="exportLoading"><font-awesome-icon icon="file-pdf" /> PDF</button>
        <button class="btn-outline" @click="$router.push($url('/hr/exits/pipeline'))">
          <font-awesome-icon icon="columns" /> Pipeline
        </button>
        <button class="btn-primary-action" @click="$router.push($url('/hr/exits/create'))">
          <font-awesome-icon icon="plus" /> Initiate Exit
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search by name, case no…" @input="debounceLoad" />
      <select v-model="filterType" class="filter-select" @change="loadCases">
        <option value="">All Types</option>
        <option value="resignation">Resignation</option>
        <option value="termination">Termination</option>
        <option value="retirement">Retirement</option>
        <option value="redundancy">Redundancy</option>
        <option value="death">Death</option>
      </select>
      <select v-model="filterStatus" class="filter-select" @change="loadCases">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="pending_approval">Pending Approval</option>
        <option value="approved">Approved</option>
        <option value="in_progress">In Progress</option>
        <option value="clearance">Clearance</option>
        <option value="fnf_pending">F&F Pending</option>
        <option value="completed">Completed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <select class="filter-select" v-model="length" @change="page = 1; loadCases()">
        <option :value="5">5 / page</option>
        <option :value="10">10 / page</option>
        <option :value="20">20 / page</option>
        <option :value="50">50 / page</option>
        <option :value="100">100 / page</option>
        <option :value="200">200 / page</option>
        <option :value="500">500 / page</option>
        <option :value="1000">1000 / page</option>
      </select>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 6" :key="n" class="sk-row"></div>
    </div>

    <div v-else>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Case No</th>
              <th>Employee</th>
              <th>Type</th>
              <th>Status</th>
              <th>Exit Date</th>
              <th>LWD</th>
              <th>Initiated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!cases.length">
              <td colspan="8" class="empty-row">No exit cases found.</td>
            </tr>
            <tr
              v-for="c in cases"
              :key="c.id"
              class="clickable-row"
              @click="$router.push($url('/hr/exits/' + c.id))"
            >
              <td class="case-no-cell">{{ c.case_number }}</td>
              <td>
                <div class="emp-cell">
                  <div class="emp-avatar">{{ initials(c.employee) }}</div>
                  <div>
                    <div class="emp-name">{{ fullName(c.employee) }}</div>
                    <div class="emp-no">{{ c.employee?.employee_no }}</div>
                  </div>
                </div>
              </td>
              <td><span class="type-badge" :class="'type--' + c.type">{{ typeLabel(c.type) }}</span></td>
              <td><span class="status-badge" :class="'status--' + c.status">{{ statusLabel(c.status) }}</span></td>
              <td>{{ fmtDate(c.exit_date) }}</td>
              <td>{{ fmtDate(c.last_working_day) }}</td>
              <td><span class="source-badge" :class="'source--' + c.initiated_source">{{ c.initiated_source?.toUpperCase() }}</span></td>
              <td class="action-cell" @click.stop>
                <button class="btn-view" @click="$router.push($url('/hr/exits/' + c.id))">View</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="meta.last_page > 1" class="pagination-bar">
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="page <= 1" @click="changePage(1)"><font-awesome-icon icon="angle-double-left" /></button>
          <button class="pg-btn" :disabled="page <= 1" @click="changePage(page - 1)"><font-awesome-icon icon="angle-left" /></button>
          <button v-for="p in pageRange" :key="p" class="pg-btn" :class="{ 'pg-btn--active': p === page }" @click="changePage(p)">{{ p }}</button>
          <button class="pg-btn" :disabled="page >= meta.last_page" @click="changePage(page + 1)"><font-awesome-icon icon="angle-right" /></button>
          <button class="pg-btn" :disabled="page >= meta.last_page" @click="changePage(meta.last_page)"><font-awesome-icon icon="angle-double-right" /></button>
        </div>
      </div>
    </div>

  <!-- ── Export Column Picker ── -->
  <transition name="fade">
    <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
      <div class="col-picker-modal">
        <div class="col-picker-header">
          <div>
            <h3 class="col-picker-title">Select Columns</h3>
            <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
          </div>
          <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="col-picker-actions">
          <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
          <span class="col-picker-sep">·</span>
          <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
          <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
        </div>
        <div class="col-picker-grid">
          <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
            <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
            <span class="col-picker-label">{{ col.label }}</span>
          </label>
        </div>
        <div class="col-picker-footer">
          <button class="col-picker-btn-ghost" @click="showExportModal = false">Cancel</button>
          <button class="col-picker-btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
            Export {{ pendingExportFmt.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'ExitCases',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      cases: [],
      meta: { current_page: 1, last_page: 1, total: 0 },
      page: 1,
      length: 5,
      search: '',
      filterType: '',
      filterStatus: '',
      debounceTimer: null,
      exportLoading: false,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'case_number',      label: 'Case No',   pdfWidth: 85 },
        { key: 'employee',         label: 'Employee',  pdfWidth: 115 },
        { key: 'type',             label: 'Type',      pdfWidth: 100 },
        { key: 'status',           label: 'Status',    pdfWidth: 115 },
        { key: 'exit_date',        label: 'Exit Date', pdfWidth: 100 },
        { key: 'last_working_day', label: 'LWD',       pdfWidth: 100 },
        { key: 'initiated',        label: 'Initiated', pdfWidth: 80 },
      ],
    }
  },
  computed: {
    pageRange() {
      const cur = this.page, last = this.meta.last_page || 1;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },
  created() { this.loadCases() },
  methods: {
    async loadCases() {
      this.loading = true
      try {
        const r = await axios.get('exits', {
          params: { page: this.page, per_page: this.length, search: this.search || undefined, type: this.filterType || undefined, status: this.filterStatus || undefined },
        })
        const payload  = r.data.data
        this.cases = payload?.data || (Array.isArray(payload) ? payload : [])
        this.meta  = {
          current_page: payload?.current_page || this.page,
          last_page:    payload?.last_page    || 1,
          total:        payload?.total        || 0,
        }
      } catch { this.toast.error('Failed to load exit cases.') }
      finally { this.loading = false }
    },
    debounceLoad() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => { this.page = 1; this.loadCases() }, 350)
    },
    changePage(p) { this.page = p; this.loadCases() },
    fullName(emp) { return emp ? `${emp.first_name} ${emp.last_name}` : '—' },
    initials(emp) { return emp ? `${emp.first_name?.[0] ?? ''}${emp.last_name?.[0] ?? ''}`.toUpperCase() : '?' },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—' },
    typeLabel(t) {
      const m = { resignation:'Resignation', termination:'Termination', retirement:'Retirement', redundancy:'Redundancy', death:'Death' }
      return m[t] || t
    },
    statusLabel(s) {
      const m = { draft:'Draft', pending_approval:'Pending Approval', approved:'Approved', in_progress:'In Progress',
                  clearance:'Clearance', fnf_pending:'F&F Pending', completed:'Completed', cancelled:'Cancelled' }
      return m[s] || s
    },

    openExportModal(format) {
      this.pendingExportFmt   = format
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key)
      this.showExportModal    = true
    },

    getExitValue(c, key) {
      switch (key) {
        case 'case_number':      return c.case_number || '—'
        case 'employee':         return c.employee ? `${c.employee.first_name} ${c.employee.last_name}` : '—'
        case 'type':             return this.typeLabel(c.type)
        case 'status':           return this.statusLabel(c.status)
        case 'exit_date':        return this.fmtDate(c.exit_date)
        case 'last_working_day': return this.fmtDate(c.last_working_day)
        case 'initiated':        return c.initiated_source?.toUpperCase() || '—'
        default:                 return '—'
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return
      this.exportLoading   = true
      this.showExportModal = false
      try {
        const r    = await axios.get('exits', { params: { page: 1, per_page: 10000, search: this.search || undefined, type: this.filterType || undefined, status: this.filterStatus || undefined } })
        const rows = r.data.data?.data || r.data.data || []
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key))
        const headers = cols.map(c => c.label)
        const body    = rows.map(row => cols.map(c => this.getExitValue(row, c.key)))
        const date    = new Date().toISOString().slice(0, 10)

        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n')
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
          const url  = URL.createObjectURL(blob)
          const a    = document.createElement('a')
          a.href = url; a.download = `exit-cases-${date}.csv`; a.click()
          URL.revokeObjectURL(url)
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
          const usable   = 761
          const scale    = usable / cols.reduce((s, c) => s + c.pdfWidth, 0)
          const colStyles = {}
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale } })
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69)
          doc.text('Exit Cases', 40, 46)
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176)
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62)
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize:7.5, cellPadding:3.5, textColor:[26,43,69], overflow:'ellipsize' },
            headStyles: { fillColor:[0,74,173], textColor:255, fontStyle:'bold', fontSize:7.5 },
            alternateRowStyles: { fillColor:[248,250,252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left:40, right:40 },
            didDrawPage(d) {
              doc.setFontSize(8); doc.setTextColor(192,202,214)
              doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height-16, { align:'center' })
            },
          })
          doc.save(`exit-cases-${date}.pdf`)
        }
      } catch {
        this.toast.error('Export failed. Please try again.')
      } finally {
        this.exportLoading = false
      }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; gap:10px; flex-wrap:wrap; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover { background:#003b8e; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f0f4ff; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { flex:1; min-width:180px; padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; background:#fff; font-family:inherit; cursor:pointer; }

.table-wrap { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table th { background:#f8fafc; padding:10px 14px; text-align:left; font-size:11px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table td { padding:12px 14px; border-bottom:1px solid #f0f4f8; color:#1a2b45; vertical-align:middle; }
.clickable-row { cursor:pointer; transition:background .12s; }
.clickable-row:hover { background:#f8fafc; }
.empty-row { text-align:center; color:#aab8cc; padding:32px; }

.case-no-cell { font-weight:700; color:#004AAD; font-size:12px; }
.emp-cell  { display:flex; align-items:center; gap:10px; }
.emp-avatar { width:32px; height:32px; border-radius:50%; background:#004AAD; color:#fff; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-name  { font-weight:600; font-size:13px; }
.emp-no    { font-size:11px; color:#7a90b0; }
.action-cell { text-align:right; }
.btn-view  { font-size:12px; font-weight:600; color:#004AAD; background:none; border:1.5px solid #004AAD; border-radius:6px; padding:4px 12px; cursor:pointer; font-family:inherit; }
.btn-view:hover { background:#f0f4ff; }

.type-badge { font-size:10px; font-weight:600; padding:3px 8px; border-radius:6px; white-space:nowrap; }
.type--resignation  { background:#fef3c7; color:#92400e; }
.type--termination  { background:#fee2e2; color:#991b1b; }
.type--retirement   { background:#e0f2fe; color:#0369a1; }
.type--redundancy   { background:#f3e8ff; color:#6b21a8; }
.type--death        { background:#f1f5f9; color:#475569; }

.status-badge { font-size:10px; font-weight:600; padding:3px 8px; border-radius:6px; white-space:nowrap; }
.status--draft           { background:#f1f5f9; color:#475569; }
.status--pending_approval { background:#fef3c7; color:#92400e; }
.status--approved        { background:#dcfce7; color:#166534; }
.status--in_progress     { background:#dbeafe; color:#1e40af; }
.status--clearance       { background:#fce7f3; color:#9d174d; }
.status--fnf_pending     { background:#fee2e2; color:#991b1b; }
.status--completed       { background:#dcfce7; color:#166534; }
.status--cancelled       { background:#f1f5f9; color:#6b7280; }

.source-badge { font-size:10px; font-weight:700; padding:2px 7px; border-radius:5px; }
.source--ess { background:#e0f2fe; color:#0369a1; }
.source--hr  { background:#f3e8ff; color:#6b21a8; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 16px; margin-top:4px; }
.pg-info     { font-size:13px; color:#7a90b0; }
.pg-controls { display:flex; gap:4px; }
.pg-btn      { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; font-size:13px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; font-family:inherit; }
.pg-btn:hover:not(:disabled) { background:#f0f4fb; border-color:#c5d6f5; color:#004AAD; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-btn--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }
.pg-btn--active:hover { background:#004AAD; color:#fff; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:56px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.btn-export-sm { display:inline-flex; align-items:center; gap:5px; background:#fff; color:#16a34a; border:1px solid #bbf7d0; font-size:12px; font-weight:600; padding:0 12px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-export-sm:hover:not(:disabled) { background:#f0fdf4; }
.btn-export-sm--pdf { color:#dc2626; border-color:#fecaca; }
.btn-export-sm--pdf:hover:not(:disabled) { background:#fff5f5; }
.btn-export-sm:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.col-picker-btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
.col-picker-btn-ghost:hover { background:#e4eaf2; }
.col-picker-btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; transition:background .15s; }
.col-picker-btn-primary:hover:not(:disabled) { background:#003b8e; }
.col-picker-btn-primary:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>
